import { Box, Button, Container, Divider, Grid, useTheme, Chip, Popper, Paper, Typography, Skeleton } from '@mui/material';
import CustomFilterButton from 'components/Fileds/CustomFilterButton';
import CustomSearch from 'components/Fileds/CustomSearch';
import NoItemsCreated from 'components/NoItemsCreated/NoItemsCreated';
import NoSearchFound from 'components/NoSearchFound/NoSearchFound';
import POSHeader from 'components/POSHeader';
import CommonDataGrid from 'components/TableListing/CommonTable';
import React, { useState, useMemo, useEffect } from 'react'
import { ThemeProvider } from 'theme/ThemeContext2';
import NoOrders from "assests/empty-order.svg";
import NoSearchOrder from "assests/no-search-order.svg";
import { ordersFilterConfigs } from 'utils/constant';
import { isOrderTypeFlagAllowed } from 'utils/function';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CustomTypography from 'components/Fileds/CustomTypography';

import { DateRangePicker, defaultStaticRanges } from 'react-date-range';
import { addDays, startOfMonth, endOfMonth, subMonths, format, startOfWeek, endOfWeek, subWeeks, } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import enUS from 'date-fns/locale/en-US';

const PastOrdersContainer = ({ setFilter, filter, searchQuery,
    columns, rows, totalCount, loading, paginationModel, setPaginationModel, setSearchQuery, searchLoader, handleBack,
    presets,
    handleSelect,
    handleButtonClick,
    selectionRange,
    setPickerOpen,
    pickerOpen,
    anchorEl,
    setSelectionRange,
    orderTypeFlags
}) => {
    const theme = useTheme();

    // Show skeleton while initial order type flags are loading to avoid button flash
    const isFlagsLoading = !orderTypeFlags && loading;

    // Filter visible order type tab buttons based on API order_type_flags
    const visibleFilterConfigs = useMemo(() => {
        return ordersFilterConfigs.filter((f) => isOrderTypeFlagAllowed(f.id, orderTypeFlags));
    }, [orderTypeFlags]);

    // If currently selected filter becomes hidden, switch gracefully to 'All'
    useEffect(() => {
        if (filter && filter !== 'All' && visibleFilterConfigs.length > 0) {
            const isVisible = visibleFilterConfigs.some((f) => f.id === filter);
            if (!isVisible) {
                setFilter('All');
            }
        }
    }, [filter, visibleFilterConfigs, setFilter]);

    // Determine which empty state to show
    const showNoData = !loading && rows.length === 0;
    const isSearchEmpty = searchQuery.trim().length > 0;


    const monthStaticRanges = presets.map(preset => ({
        label: preset.label,
        range: () => ({
            startDate: preset.range.startDate,
            endDate: preset.range.endDate,
        }),
        isSelected: range =>
            range.startDate?.getTime() === preset.range.startDate.getTime() &&
            range.endDate?.getTime() === preset.range.endDate.getTime(),
        color: '#3d91ff'
    }));

    let matchedPreset;
    const getChipLabel = (selectionRange, presets) => {
        const { startDate, endDate } = selectionRange;
        if (!startDate || !endDate) return 'Apply Date Filter';

        // see if it matches one of the presets exactly
        matchedPreset = presets.find(p =>
            p.range.startDate.getTime() === startDate.getTime() &&
            p.range.endDate.getTime() === endDate.getTime()
        );


        if (matchedPreset?.label.includes('Month')) {
            // e.g. “Showing orders of June 2025”
            return (
                <Typography className='date-chip-label' variant='body2'>
                    Showing orders of <b>{format(startDate, 'MMMM yyyy')}</b>
                </Typography>
            )
        }

        // custom range: “Showing orders of 01 Jul 2025 to 04 Jul 2025”
        return (
            <Typography variant='body2' className='date-chip-label'>
                Showing orders of <b>{format(startDate, 'dd MMM yyyy')} to {format(endDate, 'dd MMM yyyy')}</b>
            </Typography>
        )
    };

    return (
        <ThemeProvider>
            <Box className='order-listing' sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
                <POSHeader />
                <Box className="pos-container">
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={{
                            px: { xs: 1, sm: 2, md: 3 },
                            py: 2,
                            maxWidth: "1400px",
                            mx: "auto",
                        }}
                    >
                        <Box className='d-flex align-items-center justify-content-between' sx={{ height: '55px' }}>
                            <Box className='d-flex align-items-center' sx={{ gap: 2 }}>
                                <Box className='back-rounded-icon d-flex align-items-center justify-content-center cursor-pointer'
                                    onClick={handleBack}
                                    sx={{ border: '1px solid #4D4D4D', borderRadius: '50%', width: '30px', height: '30px' }}>
                                    <ChevronLeftIcon />
                                </Box>
                                <CustomTypography
                                    label={"Past Orders"}
                                    variant={"h5"}
                                    sx={{
                                        fontSize: '24px', fontWeight: 600,
                                        color: theme.palette.typographyLabel.main, mb: 0
                                    }}
                                />
                            </Box>

                            <Box sx={{ position: 'relative' }}>
                                {!selectionRange?.startDate ? (
                                    <Button
                                        variant="outlined"
                                        startIcon={<CalendarMonthIcon />}
                                        onClick={handleButtonClick}
                                        sx={{ textTransform: 'none', lineHeight: '1.5' }}
                                    >
                                        Apply Date Filter
                                    </Button>
                                ) : (
                                    <Box className='d-flex align-items-center' sx={{ gap: '20px' }}>
                                        <Chip
                                            label={getChipLabel(selectionRange, presets)}
                                            onDelete={() => {
                                                setSelectionRange({ startDate: null, endDate: null, key: 'selection' });
                                                setPickerOpen(false);
                                            }}
                                            className='chip-label-container'
                                        />
                                        <Button
                                            variant="outlined"
                                            startIcon={<CalendarMonthIcon />}
                                            onClick={handleButtonClick}
                                            sx={{ lineHeight: '1.5', textTransform: 'none' }}
                                        >
                                            {matchedPreset ? matchedPreset.label : 'Custom Range'}
                                        </Button>
                                    </Box>
                                )}

                                <Popper
                                    open={pickerOpen}
                                    anchorEl={anchorEl}
                                    placement="bottom-start"
                                    onClose={() => setPickerOpen(false)}
                                    disablePortal={false}
                                    modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                                >
                                    <Paper elevation={3}>
                                        <DateRangePicker
                                            locale={enUS}
                                            onChange={handleSelect}
                                            // showSelectionPreview={true}
                                            months={2}
                                            direction="horizontal"
                                            showDateDisplay={false}
                                            showPreview={false}
                                            staticRanges={monthStaticRanges}
                                            inputRanges={[]}
                                            initialFocusedRange={[0, 0]}
                                            moveRangeOnFirstSelection={false}
                                            ranges={[selectionRange?.startDate ? selectionRange : {
                                                startDate: new Date(),
                                                endDate: new Date(),
                                                key: 'selection',
                                                color: '#000'
                                            }]}
                                        />
                                    </Paper>
                                </Popper>
                            </Box>
                        </Box>
                        <Divider sx={{ mt: 2 }} />

                        <Box className='d-flex align-items-center justify-content-between' sx={{ mt: '20px', gap: 1 }}>
                            <Box className='d-flex' sx={{ gap: '15px' }}>
                                {isFlagsLoading ? (
                                    [55, 95, 80, 80, 105].map((w, index) => (
                                        <Skeleton
                                            key={index}
                                            variant="rectangular"
                                            animation="wave"
                                            width={w}
                                            height={30}
                                            sx={{ borderRadius: '5px' }}
                                        />
                                    ))
                                ) : (
                                    visibleFilterConfigs.map((f) => (
                                        <CustomFilterButton
                                            key={f.id}
                                            label={f.label}
                                            color={f.color}
                                            backgroundColor={f.backgroundColor}
                                            selected={filter === f.id}
                                            onClick={() => setFilter(f.id)}
                                        />
                                    ))
                                )}
                            </Box>

                            <Box className='d-flex align-items-center' sx={{ gap: '10px' }}>
                                <CustomSearch
                                    onSearchChange={setSearchQuery}
                                    searchQuery={searchQuery}
                                    placeholder={"Search Order"}
                                    isLoading={searchLoader}
                                />
                            </Box>
                        </Box>
                    </Container>
                </Box>
                <Box className='order-table' sx={{ flexGrow: 1 }}>
                    {showNoData ? (
                        isSearchEmpty ? (
                            <NoSearchFound
                                title="No orders that match your search!"
                                subtitle="Please try using different Order Number or Customer Name for searching."
                                img={NoSearchOrder}
                                width="250px"
                                height="250px"
                            />
                        ) : (
                            <NoItemsCreated
                                title="No orders to attend!"
                                subtitle="Take a breath and we will show as soon as we have received something!"
                                img={NoOrders}
                                width="250px"
                                height="250px"
                            />
                        )
                    ) : (
                        <CommonDataGrid
                            columns={columns}
                            rows={rows}
                            rowCount={totalCount}
                            loading={loading}
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                        />
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    )
}

export default PastOrdersContainer
